/**
 * Main Axios API Instance
 * 
 * Phase 2-B: Session Cookie Authentication (Hardened)
 * - withCredentials: true enables sending HttpOnly cookies with requests
 * - access-token header REMOVED - auth is now via session cookie only
 * - api-key header KEPT for backward compatibility
 * 
 * 401 Handling (Strategy 1 - Explicit Error Code Matching):
 * - Only redirect to /login for known session-invalid error codes
 * - Prevents accidental logout on "business 401s"
 * - Special handling for /auth/me to prevent redirect loops
 */

import axios from "axios";
import { BASE_URL, DEFAULT_KEY } from "../config/config";
import { toast } from "react-toastify";

// ============================================================================
// 401 HARDENING HELPERS
// ============================================================================

/**
 * Legacy numeric codes that indicate session is truly invalid
 * NOTE: 1042 is handled separately with special behavior
 */
const SESSION_INVALID_NUMERIC_CODES = [
  1002,  // Token invalid / session not found
  1076,  // Token expired
];

/**
 * String-based error codes that indicate session is truly invalid
 */
const SESSION_INVALID_STRING_CODES = [
  'SESSION_INVALID',
  'TOKEN_EXPIRED',
  'TOKEN_REVOKED',
];

/**
 * Extract legacy error code from various response shapes
 * Handles both numeric and string codes, normalizes to number when possible
 * 
 * @param {Object} error - Axios error object
 * @returns {{ numericCode: number|null, stringCode: string|null }}
 */
const extractLegacyCode = (error) => {
  const response = error?.response;
  
  // Try to extract numeric code from various nested structures
  const rawCode = 
    response?.data?.data?.data?.code ||
    response?.data?.data?.code ||
    response?.data?.code ||
    null;
  
  // Try to extract string error code
  const stringCode =
    response?.data?.data?.data?.errorCode ||
    response?.data?.data?.errorCode ||
    response?.data?.errorCode ||
    null;
  
  // Normalize numeric code (handle string "1002" -> 1002)
  let numericCode = null;
  if (rawCode !== null) {
    const parsed = typeof rawCode === 'number' ? rawCode : parseInt(rawCode, 10);
    if (!isNaN(parsed)) {
      numericCode = parsed;
    }
  }
  
  return { numericCode, stringCode };
};

/**
 * Check if the request URL is the session check endpoint
 * We handle /auth/me 401s specially to prevent redirect loops
 * 
 * @param {Object} error - Axios error object
 * @returns {boolean}
 */
const isAuthMeRequest = (error) => {
  const url = error?.config?.url || '';
  return url.includes('auth/me') || url.endsWith('/auth/me');
};

/**
 * Determine if a 401 error should force logout and redirect
 * 
 * @param {Object} error - Axios error object
 * @returns {boolean} - True ONLY if we should force logout
 */
const shouldForceLogoutOn401 = (error) => {
  // Must be a 401 response
  if (error?.response?.status !== 401) {
    return false;
  }
  
  // Never force logout on /auth/me - let App.js handle bootstrap
  if (isAuthMeRequest(error)) {
    return false;
  }
  
  const { numericCode, stringCode } = extractLegacyCode(error);
  
  // Check for known session-invalid numeric codes
  if (numericCode !== null && SESSION_INVALID_NUMERIC_CODES.includes(numericCode)) {
    return true;
  }
  
  // Check for known session-invalid string codes
  if (stringCode !== null && SESSION_INVALID_STRING_CODES.includes(stringCode)) {
    return true;
  }
  
  // Unknown 401 - do NOT force logout (safer, let component handle)
  return false;
};

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Required for session cookies
});

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

instance.interceptors.response.use(
  (response) => {
    if (response.status === 200) {
      return response;
    }
  },
  (error) => {
    const status = error?.response?.status;
    
    if (status === 401) {
      const { numericCode } = extractLegacyCode(error);
      
      // Special case: error code 1042 - preserve EXACT existing behavior
      // This is a specific business case that must not change
      if (numericCode === 1042) {
        localStorage.clear();
        window.location.href = "/login"; 
        return error.response?.data?.data;
      }
      
      // Hardened 401 handling: only logout on known session-invalid codes
      if (shouldForceLogoutOn401(error)) {
        localStorage.clear();
        window.location.href = "/login"; 
        return Promise.reject(error);
      }
      
      // For unknown 401s (business 401) or /auth/me: just reject, no logout
      return Promise.reject(error);
      
    } else if (status === 403) {
      // Handle KYC restriction error - don't logout, show message instead
      const errorData = error?.response?.data?.data;
      if (errorData?.errorCode === "KYC_RESTRICTED") {
        toast.error(errorData?.message || "This action requires KYC approval.");
        return {
          data: {
            status: false,
            kycRestricted: true,
            data: errorData
          }
        };
      }
      if (error?.response?.data?.description) {
        toast.error(error?.response?.data?.description);
      }
      // Do NOT force logout on 403 - just reject and let component handle
      return Promise.reject(error);
      
    } else if (status === 500) {
      // Server error - don't automatically logout
      return Promise.reject(error);
      
    } else {
      // Other errors - return data if available
      return error.response?.data;
    }
  }
);

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

instance.interceptors.request.use((config) => {
  config.headers["Accept"] = "application/json";
  
  // Don't set Content-Type for FormData - let the browser set it with boundary
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  
  // Keep api-key header for backward compatibility
  config.headers["api-key"] = DEFAULT_KEY;
  
  // NOTE: access-token header REMOVED in Phase 2-B
  // Authentication is now handled via HttpOnly session cookie (icare_user_sid)
  // The cookie is automatically sent by the browser due to withCredentials: true
  
  return config;
});

const API = instance;

export default API;
